import { Link } from 'react-router-dom';

import SEO from '@/components/SEO';
import usePageContent from '@/lib/usePageContent';

const decodeText = (value) => String(value || '')
  .replace(/&apos;/g, "'")
  .replace(/&#39;/g, "'")
  .replace(/&quot;/g, '"')
  .replace(/&amp;/g, '&');

const About = () => {
  const pageContent = usePageContent('about');
  const startupRecognitionLine = 'KalaManch.art is a product of Tivaro LLP, recognized by DPIIT under the Startup India initiative, Government of India, and recognized as a startup by the Department of IT & Electronics, Government of Uttar Pradesh.';

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="about-page">
      <SEO
        title="About Kalamanch.art"
        description="Learn how Kalamanch.art connects Indian artists with collaborators, organizers, production houses, institutions, and creative opportunities."
        keywords="about Kalamanch, Indian creative marketplace, artist discovery platform, verified artists India"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-16">
          <h1 className="launch-heading mx-auto max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-bold gradient-text mb-6" data-testid="about-heading">
            {decodeText(pageContent.hero_title || 'About KalaManch.Art')}
          </h1>
          <p className="launch-copy text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto" data-testid="about-subtitle">
            {decodeText(pageContent.hero_subtitle || "Empowering India's creative community through technology, trust, and real opportunity.")}
          </p>
          <p className="mx-auto mt-6 max-w-4xl rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm font-bold leading-6 text-emerald-800">
            {startupRecognitionLine}
          </p>
        </div>

        <div className="mb-20">
          <div className="bg-gradient-to-br from-orange-50 to-purple-50 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6" data-testid="vision-heading">
              {pageContent.vision_title || 'Our Vision'}
            </h2>
            <p className="text-base text-gray-700 leading-relaxed mb-4">
              {pageContent.vision_body || "At KalaManch.art, our vision is to build India's largest and most inclusive creative marketplace-where every artist, regardless of background or location, gets a fair chance to showcase talent and earn opportunities."}
            </p>
            <p className="text-base text-gray-700 leading-relaxed">
              {pageContent.vision_body_2 || 'We aim to remove middlemen, eliminate barriers, and create a direct bridge between talent and those seeking it.'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="market-section">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FF6F00] to-[#FF8F00] rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Huge Untapped Indian Market</h2>
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              India is home to millions of talented individuals—from singers and dancers to writers, performers, and event artists—yet a large percentage of this talent remains undiscovered.
            </p>
            <p className="text-base text-gray-600 leading-relaxed">
              Especially in small towns and villages, artists lack exposure, networks, and access to opportunities. KalaManch.art unlocks this massive untapped market by bringing talent from every corner of India onto one unified platform.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="marketplace-section">
            <div className="w-16 h-16 bg-gradient-to-br from-[#4A148C] to-[#6A1B9A] rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Social Marketplace Model</h2>
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              KalaManch.art operates as a zero-commission social marketplace, where artists can showcase their profiles, and clients can discover and connect with them directly.
            </p>
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              No hidden fees. No middlemen. Just real connections and real opportunities.
            </p>
            <p className="text-base text-gray-600 leading-relaxed font-medium">This model empowers artists to:</p>
            <ul className="mt-4 space-y-2 text-base text-gray-600">
              <li className="flex items-start"><span className="text-[#FF6F00] mr-2">•</span><span>Get hired directly</span></li>
              <li className="flex items-start"><span className="text-[#FF6F00] mr-2">•</span><span>Build their personal brand</span></li>
              <li className="flex items-start"><span className="text-[#FF6F00] mr-2">•</span><span>Access both small gigs and large projects</span></li>
            </ul>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="ecosystem-section">
            <div className="w-16 h-16 bg-gradient-to-br from-[#00BFA5] to-[#00D9B5] rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Safe & Verified Ecosystem</h2>
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              At KalaManch.art, trust and safety are at the core of everything we build. We are creating a secure environment where both artists and clients can connect with confidence.
            </p>
            <p className="text-base text-gray-600 leading-relaxed mb-4 font-medium">Our ecosystem is designed to ensure:</p>
            <ul className="space-y-2 text-base text-gray-600">
              <li className="flex items-start"><span className="text-[#00BFA5] mr-2">•</span><span>Verified artist profiles</span></li>
              <li className="flex items-start"><span className="text-[#00BFA5] mr-2">•</span><span>Authentic client interactions</span></li>
              <li className="flex items-start"><span className="text-[#00BFA5] mr-2">•</span><span>Transparent communication</span></li>
              <li className="flex items-start"><span className="text-[#00BFA5] mr-2">•</span><span>Protection from fraud and misuse</span></li>
            </ul>
            <p className="text-base text-gray-600 leading-relaxed mt-4">
              Because when trust is strong, opportunities grow faster.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="features-section">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FF6F00] to-[#4A148C] rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Future Features</h2>
            <p className="text-base text-gray-600 leading-relaxed mb-4">
              KalaManch.art is just getting started. We are building powerful tools to transform how artists grow and get discovered.
            </p>
            <p className="text-base text-gray-600 leading-relaxed mb-4 font-medium">Upcoming features include:</p>
            <ul className="space-y-3 text-base text-gray-600">
              <li className="flex items-start"><span className="mr-2">🎯</span><span>AI-powered talent discovery & recommendations</span></li>
              <li className="flex items-start"><span className="mr-2">⭐</span><span>Ratings & reviews for credibility</span></li>
              <li className="flex items-start"><span className="mr-2">📅</span><span>Booking & scheduling system for gigs</span></li>
              <li className="flex items-start"><span className="mr-2">💬</span><span>In-app chat for seamless communication</span></li>
              <li className="flex items-start"><span className="mr-2">🎥</span><span>Portfolio videos & live performance showcases</span></li>
              <li className="flex items-start"><span className="mr-2">💼</span><span>Dedicated dashboards for artists and clients</span></li>
              <li className="flex items-start"><span className="mr-2">🌐</span><span>Multi-language support to reach every corner of India</span></li>
            </ul>
            <p className="text-base text-gray-600 leading-relaxed mt-4">
              Our goal is to continuously evolve into India's most powerful platform for creative professionals.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#FF6F00] to-[#4A148C] rounded-3xl p-6 sm:p-10 lg:p-12 text-white text-center" data-testid="join-movement-section">
          <h2 className="launch-heading mx-auto max-w-3xl text-3xl sm:text-4xl font-bold mb-5">{pageContent.cta_title || 'Join the Movement'}</h2>
          <p className="launch-copy text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            {pageContent.cta_description || "Whether you're an artist looking for opportunities or a client seeking talent, KalaManch.art is your gateway to India's vibrant creative ecosystem."}
          </p>
          <Link to="/join" data-testid="get-started-link">
            <button
              data-testid="get-started-button"
              className="btn-primary bg-white text-[#FF6F00] hover:bg-gray-100"
            >
              Get Started Today
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
